import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export function formatCurrency(amount, currency = 'EUR') {
    return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
    }).format(amount);
}

export function formatDate(dateString) {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(new Date(dateString));
}

export function formatDateTime(dateString) {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(dateString));
}

export function formatRelativeDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    if (diffDays < 30) return `Il y a ${Math.floor(diffDays / 7)} semaines`;
    return formatDate(dateString);
}

export const MANDATE_STATUS_LABELS = {
    brouillon: 'Brouillon',
    draft: 'Brouillon',
    a_signer: 'À signer',
    en_signature: 'En signature',
    actif: 'Actif',
    active: 'Actif',
    generated: 'PDF généré',
    sent: 'Envoyé',
    acknowledged: 'Signé',
    offre: 'Offre reçue',
    compromis: 'Compromis',
    vendu: 'Vendu',
    sold: 'Vendu',
    closed: 'Vendu',
    terminated: 'Vendu',
    denonce: 'Dénoncé',
    denounced: 'Dénoncé',
    expire: 'Expiré',
    expired: 'Expiré',
    cancelled: 'Annulé',
};

export const MANDATE_TYPE_LABELS = {
    simple: 'Simple',
    semi_exclusif: 'Semi-exclusif',
    exclusif: 'Exclusif',
    co_exclusif: 'Co-exclusif',
    partage: 'Partagé',
};

export const PROPERTY_TYPE_LABELS = {
    appartement: 'Appartement',
    apartment: 'Appartement',
    maison: 'Maison',
    house: 'Maison',
    villa: 'Villa',
    mas: 'Mas',
    village_house: 'Maison de village',
    terrain: 'Terrain',
    land: 'Terrain',
    local_commercial: 'Local commercial',
    commercial: 'Local commercial',
    immeuble: 'Immeuble',
    building: 'Immeuble',
    parking: 'Parking',
    autre: 'Autre',
    other: 'Autre',
};

export const DPE_RATINGS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

export function getStatusColor(status) {
    const colors = {
        brouillon: 'bg-slate-500/15 text-slate-300',
        draft: 'bg-slate-500/15 text-slate-300',
        a_signer: 'bg-amber-500/15 text-amber-300',
        en_signature: 'bg-blue-500/15 text-blue-300',
        actif: 'bg-emerald-500/15 text-emerald-300',
        active: 'bg-emerald-500/15 text-emerald-300',
        generated: 'bg-blue-500/15 text-blue-300',
        sent: 'bg-blue-500/15 text-blue-300',
        acknowledged: 'bg-emerald-500/15 text-emerald-300',
        offre: 'bg-purple-500/15 text-purple-300',
        compromis: 'bg-indigo-500/15 text-indigo-300',
        vendu: 'bg-emerald-500/20 text-emerald-200',
        sold: 'bg-emerald-500/20 text-emerald-200',
        closed: 'bg-emerald-500/20 text-emerald-200',
        terminated: 'bg-emerald-500/20 text-emerald-200',
        denonce: 'bg-red-500/15 text-red-300',
        denounced: 'bg-red-500/15 text-red-300',
        expire: 'bg-white/10 text-white/50',
        expired: 'bg-white/10 text-white/50',
        cancelled: 'bg-white/10 text-white/50',
    };
    return colors[status] || 'bg-white/10 text-white/60';
}

/**
 * Labels propres FR pour toutes les sources de leads.
 * Couvre les valeurs envoyées par le backend ET les seeds démo
 * (web_lead, porte_a_porte, appel_entrant…).
 */
export const LEAD_SOURCE_LABELS = {
    site_internet: 'Site internet',
    web: 'Site web',
    web_lead: 'Site web',
    instagram: 'Instagram',
    facebook: 'Facebook',
    social_media: 'Réseaux sociaux',
    bouche_a_oreille: 'Bouche-à-oreille',
    recommandation: 'Recommandation',
    referral: 'Recommandation',
    pige: 'Pige immobilière',
    prospection: 'Prospection terrain',
    porte_a_porte: 'Porte-à-porte',
    door_knock: 'Porte-à-porte',
    panneau: 'Panneau / Vitrine',
    vitrine: 'Vitrine agence',
    partenaire: 'Partenaire',
    appel_entrant: 'Appel entrant',
    inbound: 'Appel entrant',
    email: 'Email',
    flyer: 'Flyer',
    autre: 'Autre',
    other: 'Autre',
    non_renseigne: 'Non renseigné',
    discovery_converted: 'Découverte signée',
};

export function leadSourceLabel(src) {
    if (!src) return 'Non renseigné';
    return LEAD_SOURCE_LABELS[src] || src.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}


export function getDPEColor(rating) {
    const colors = {
        A: 'bg-green-500',
        B: 'bg-lime-500',
        C: 'bg-yellow-500',
        D: 'bg-orange-400',
        E: 'bg-orange-500',
        F: 'bg-red-400',
        G: 'bg-red-600',
    };
    return colors[rating] || 'bg-gray-400';
}

export function getInitials(firstName, lastName) {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

export function truncateText(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
}
